import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { articles } from '../data/articles';
export function ArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const article = articles.find(a => a.slug === slug);
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    // Small delay to let the page render, then scroll to field-notes
    setTimeout(() => {
      const el = document.getElementById('field-notes');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  if (!article) {
    return <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center" data-id="element-323">
        <div className="text-center" data-id="element-324">
          <h1 className="text-4xl font-serif font-bold text-[#1A1A1A] mb-4" data-id="element-325">
            Article Not Found
          </h1>
          <a href="/" onClick={handleBack} className="text-[#A07656] hover:text-[#B8896A] transition-colors font-semibold" data-id="element-326">
            ← Back to Field Notes
          </a>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#F5F3F0]" data-id="element-327">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 px-6 py-5 flex justify-between items-center bg-[#F5F3F0]/95 backdrop-blur-md border-b border-[#E8E4DF]/80" data-id="element-328">
        <a href="/" onClick={handleBack} data-id="element-329">
          <img src="https://cdn.magicpatterns.com/uploads/rsVSuidRepyz9oxNYVg3rK/Iron_Ridge_Logo.png" alt="Iron Ridge Custom Homes" className="h-12 md:h-14 w-auto" data-id="element-330" />
        </a>
        <a href="/" onClick={handleBack} className="flex items-center gap-2 text-sm uppercase tracking-widest font-medium text-[#1A1A1A] hover:text-[#A07656] transition-colors" data-id="element-331">
          <ArrowLeft className="w-4 h-4" data-id="element-332" />
          Back
        </a>
      </header>

      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden" data-id="element-333">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" data-id="element-334" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/30 to-[#1A1A1A]/10" data-id="element-335" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 lg:p-20" data-id="element-336">
          <div className="max-w-3xl mx-auto" data-id="element-337">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-[#A07656] text-xs font-bold uppercase tracking-[0.2em] mb-4 block" data-id="element-338">
              {article.category}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-[1.15]" data-id="element-339">
              {article.title}
            </motion.h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <motion.article initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.2
    }} className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24" data-id="element-340">
        {article.content ? <div className="article-content" dangerouslySetInnerHTML={{
        __html: article.content
      }} data-id="element-341" /> : <p className="text-[#595959] text-lg leading-relaxed" data-id="element-342">
            {article.excerpt}
          </p>}

        {/* Back to articles */}
        <div className="mt-20 pt-10 border-t-2 border-[#E8E4DF]" data-id="element-343">
          <a href="/" onClick={handleBack} className="inline-flex items-center gap-2 text-[#A07656] hover:text-[#B8896A] transition-colors font-semibold text-sm uppercase tracking-[0.15em]" data-id="element-344">
            <ArrowLeft className="w-4 h-4" data-id="element-345" />
            Back to Field Notes
          </a>
        </div>
      </motion.article>

      {/* Footer */}
      <div className="bg-[#1A1A1A] py-8 text-center" data-id="element-346">
        <p className="text-xs text-[#D4CFC8]/40" data-id="element-347">
          &copy; {new Date().getFullYear()} Iron Ridge Custom Homes. All rights
          reserved.
        </p>
      </div>
    </div>;
}